import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from './auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from './components/page-elements';

const formatPhone = (input = '') => {
  const digits = String(input).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const initialLocationForm = {
  receiverName: '',
  projectName: '',
  projectAddress1: '',
  projectAddress2: '',
  projectCity: '',
  projectState: '',
  projectZip: '',
  siteContactName: '',
  siteContactPhone: '',
};

const validateLocation = (form) => {
  const errors = {};
  if (!form.receiverName) errors.receiverName = 'Receiver is required.';
  if (!form.projectName?.trim()) errors.projectName = 'Location name is required.';
  if (!form.projectAddress1?.trim()) errors.projectAddress1 = 'Address is required.';
  if (!form.projectCity?.trim()) errors.projectCity = 'City is required.';
  if (!form.projectState?.trim()) errors.projectState = 'State is required.';
  if (!form.projectZip?.trim()) errors.projectZip = 'ZIP is required.';
  return errors;
};

const getCustomerId = (row) => String(row?.customerName?._id || row?.customerName || '');

const Projects = ({ forcedCustomerId = '', embedded = false }) => {
  const [searchParams] = useSearchParams();
  const receiverIdParam = searchParams.get('receiverId') || '';
  const autoCreate = searchParams.get('create') === '1';

  const [projects, setProjects] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState('');
  const [createOpen, setCreateOpen] = useState(autoCreate);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [receiverFilter, setReceiverFilter] = useState(receiverIdParam || 'all');

  const [createForm, setCreateForm] = useState({ ...initialLocationForm, receiverName: receiverIdParam });
  const [editForm, setEditForm] = useState(initialLocationForm);

  const user = useMemo(() => getStoredUser(), []);
  const canManage = user?.role === 'admin' || user?.role === 'internal' || user?.role === 'customer';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchLocations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (receiverIdParam) params.set('receiverId', receiverIdParam);
      if (forcedCustomerId) params.set('customerId', forcedCustomerId);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE_URL}/api/projects${query}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];
      const scopedRows = forcedCustomerId
        ? rows.filter((row) => getCustomerId(row) === String(forcedCustomerId))
        : rows;
      setProjects(scopedRows);
      setLoading(false);
    } catch (err) {
      setLoadError(err.message);
      setLoading(false);
    }
  }, [receiverIdParam, forcedCustomerId]);

  const fetchReceivers = useCallback(async () => {
    try {
      const query = forcedCustomerId ? `?customerId=${forcedCustomerId}` : '';
      const response = await fetch(`${API_BASE_URL}/api/receivers${query}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      const rows = Array.isArray(data) ? data : [];
      const scopedRows = forcedCustomerId
        ? rows.filter((row) => getCustomerId(row) === String(forcedCustomerId))
        : rows;
      setReceivers(scopedRows);
    } catch {
      // No-op for receiver fetch fallback
    }
  }, [forcedCustomerId]);

  useEffect(() => {
    fetchLocations();
    fetchReceivers();
  }, [receiverIdParam, fetchLocations, fetchReceivers]);

  useEffect(() => {
    if (receiverIdParam) {
      setCreateForm((prev) => ({ ...prev, receiverName: receiverIdParam }));
    }
    setReceiverFilter(receiverIdParam || 'all');
    if (autoCreate) {
      setCreateOpen(true);
    }
  }, [receiverIdParam, autoCreate]);

  const filteredProjects = useMemo(() => {
    if (receiverFilter === 'all') return projects;
    return projects.filter((project) => {
      const projectReceiverId = project.receiverName?._id || project.receiverName || '';
      return projectReceiverId === receiverFilter;
    });
  }, [projects, receiverFilter]);

  const openEdit = (project) => {
    setSelectedProject(project);
    setEditFieldErrors({});
    setEditForm({
      receiverName: project.receiverName?._id || project.receiverName || '',
      projectName: project.projectName || '',
      projectAddress1: project.projectAddress1 || '',
      projectAddress2: project.projectAddress2 || '',
      projectCity: project.projectCity || '',
      projectState: project.projectState || '',
      projectZip: project.projectZip || '',
      siteContactName: project.siteContactName || '',
      siteContactPhone: project.siteContactPhone || '',
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setSelectedProject(null);
    setActionError('');
    setEditFieldErrors({});
  };

  const openCreate = () => setCreateOpen(true);

  const closeCreate = () => {
    setCreateOpen(false);
    setActionError('');
    setCreateFieldErrors({});
    setCreateForm({ ...initialLocationForm, receiverName: receiverIdParam || '' });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'siteContactPhone' ? formatPhone(value) : value;
    setEditForm((prev) => ({ ...prev, [name]: nextValue }));
    setEditFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'siteContactPhone' ? formatPhone(value) : value;
    setCreateForm((prev) => ({ ...prev, [name]: nextValue }));
    setCreateFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const saveCreate = async () => {
    const errors = validateLocation(createForm);
    setCreateFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...createForm,
          ...(forcedCustomerId ? { customerName: forcedCustomerId } : {}),
          siteContactPhone: formatPhone(createForm.siteContactPhone),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to create location');
      }

      const data = await response.json();
      setProjects((prev) => [data.project, ...prev]);
      closeCreate();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const saveEdit = async () => {
    if (!selectedProject?._id) return;

    const errors = validateLocation(editForm);
    setEditFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setActionError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${selectedProject._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...editForm,
          siteContactPhone: formatPhone(editForm.siteContactPhone),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to update location');
      }

      const data = await response.json();
      setProjects((prev) => prev.map((p) => (p._id === data.project._id ? data.project : p)));
      closeEdit();
    } catch (err) {
      setActionError(err.message);
    }

    setSaving(false);
  };

  const deactivateProject = async (projectId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ isActive: false }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const firstValidationError = err?.errors?.[0]?.msg;
        throw new Error(firstValidationError || err?.message || 'Failed to deactivate location');
      }

      const data = await response.json();
      setProjects((prev) => prev.map((p) => (p._id === data.project._id ? data.project : p)));
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (loading) return <LoadingPane label="Loading locations..." />;

  return (
    <Box p={embedded ? 0 : 2}>
      {!embedded ? (
        <PageHeader
          title="Locations"
          subtitle="Manage delivery locations linked to receivers."
          actions={canManage ? [<Button key="add" variant="contained" onClick={openCreate}>Add Location</Button>] : null}
        />
      ) : (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Locations</Typography>
          {canManage ? <Button variant="contained" onClick={openCreate}>Add Location</Button> : null}
        </Box>
      )}
      <ErrorBanner message={loadError || actionError} />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="receiver-filter-label">Filter by Receiver</InputLabel>
          <Select
            labelId="receiver-filter-label"
            value={receiverFilter}
            label="Filter by Receiver"
            onChange={(e) => setReceiverFilter(e.target.value)}
          >
            <MenuItem value="all">All Receivers</MenuItem>
            {receivers.map((receiver) => (
              <MenuItem key={receiver._id} value={receiver._id}>
                {receiver.receiverName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredProjects.length === 0 ? (
        <EmptyPane title="No locations found" subtitle="Add a location to start routing loads." />
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {filteredProjects.map((project) => (
            <Card key={project._id} variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.6}>
                  <Typography fontWeight={700}>{project.projectName}</Typography>
                  <Chip size="small" label={project.isActive === false ? 'Inactive' : 'Active'} color={project.isActive === false ? 'default' : 'success'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">Receiver: {project.receiverName?.receiverName || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{project.fullAddress || 'N/A'}</Typography>
                {canManage && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit location"><IconButton color="primary" onClick={() => openEdit(project)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Deactivate location"><span><IconButton color="warning" onClick={() => deactivateProject(project._id)} disabled={project.isActive === false}><BlockIcon /></IconButton></span></Tooltip>
                  </Stack>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
      <TableContainer component={Paper} elevation={0} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#ffffff' }}>
              <TableCell>Location</TableCell>
              <TableCell>Receiver</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Status</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>No locations found.</TableCell>
              </TableRow>
            ) : (
              filteredProjects.map((project) => (
                <TableRow key={project._id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{project.projectName}</Typography>
                  </TableCell>
                  <TableCell>{project.receiverName?.receiverName || 'N/A'}</TableCell>
                  <TableCell>{project.fullAddress || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={project.isActive === false ? 'Inactive' : 'Active'}
                      color={project.isActive === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Edit location">
                        <IconButton color="primary" onClick={() => openEdit(project)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate location">
                        <span>
                          <IconButton color="warning" onClick={() => deactivateProject(project._id)} disabled={project.isActive === false}>
                            <BlockIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Location</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" error={Boolean(editFieldErrors.receiverName)}>
            <InputLabel id="edit-receiver-label">Receiver</InputLabel>
            <Select labelId="edit-receiver-label" name="receiverName" value={editForm.receiverName} onChange={handleEditChange} label="Receiver">
              {receivers.map((receiver) => (
                <MenuItem key={receiver._id} value={receiver._id}>{receiver.receiverName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="normal" name="projectName" label="Location Name" value={editForm.projectName} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.projectName)} helperText={editFieldErrors.projectName} />
          <TextField margin="normal" name="projectAddress1" label="Address 1" value={editForm.projectAddress1} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.projectAddress1)} helperText={editFieldErrors.projectAddress1} />
          <TextField margin="normal" name="projectAddress2" label="Address 2" value={editForm.projectAddress2} onChange={handleEditChange} fullWidth />
          <TextField margin="normal" name="projectCity" label="City" value={editForm.projectCity} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.projectCity)} helperText={editFieldErrors.projectCity} />
          <TextField margin="normal" name="projectState" label="State" value={editForm.projectState} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.projectState)} helperText={editFieldErrors.projectState} />
          <TextField margin="normal" name="projectZip" label="Zip" value={editForm.projectZip} onChange={handleEditChange} fullWidth error={Boolean(editFieldErrors.projectZip)} helperText={editFieldErrors.projectZip} />
          <TextField margin="normal" name="siteContactName" label="Site Contact Name" value={editForm.siteContactName} onChange={handleEditChange} fullWidth />
          <TextField margin="normal" name="siteContactPhone" label="Site Contact Phone" value={editForm.siteContactPhone} onChange={handleEditChange} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>Add Location</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" error={Boolean(createFieldErrors.receiverName)}>
            <InputLabel id="create-receiver-label">Receiver</InputLabel>
            <Select labelId="create-receiver-label" name="receiverName" value={createForm.receiverName} onChange={handleCreateChange} label="Receiver">
              {receivers.map((receiver) => (
                <MenuItem key={receiver._id} value={receiver._id}>{receiver.receiverName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField margin="normal" name="projectName" label="Location Name" value={createForm.projectName} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.projectName)} helperText={createFieldErrors.projectName} />
          <TextField margin="normal" name="projectAddress1" label="Address 1" value={createForm.projectAddress1} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.projectAddress1)} helperText={createFieldErrors.projectAddress1} />
          <TextField margin="normal" name="projectAddress2" label="Address 2" value={createForm.projectAddress2} onChange={handleCreateChange} fullWidth />
          <TextField margin="normal" name="projectCity" label="City" value={createForm.projectCity} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.projectCity)} helperText={createFieldErrors.projectCity} />
          <TextField margin="normal" name="projectState" label="State" value={createForm.projectState} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.projectState)} helperText={createFieldErrors.projectState} />
          <TextField margin="normal" name="projectZip" label="Zip" value={createForm.projectZip} onChange={handleCreateChange} fullWidth error={Boolean(createFieldErrors.projectZip)} helperText={createFieldErrors.projectZip} />
          <TextField margin="normal" name="siteContactName" label="Site Contact Name" value={createForm.siteContactName} onChange={handleCreateChange} fullWidth />
          <TextField margin="normal" name="siteContactPhone" label="Site Contact Phone" value={createForm.siteContactPhone} onChange={handleCreateChange} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate}>Cancel</Button>
          <Button variant="contained" onClick={saveCreate} disabled={saving}>{saving ? 'Saving...' : 'Create Location'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Projects;

